import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuid } from 'uuid';
import type {
  Project,
  Chapter,
  View,
  EditorSettings,
  BookMetadata,
  WritingSession,
  WritingGoal,
  SectionType,
  DailyStats,
} from '../types';
import { api } from '../lib/api';
import {
  mapProjectFromApi,
  mapChapterFromApi,
  mapChapterToApi,
  type ApiProjectWithChapters,
} from '../lib/mappers';

// Debounce utility
function debounce<Args extends unknown[]>(
  fn: (...args: Args) => void | Promise<void>,
  delay: number
): (...args: Args) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args: Args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

interface AppState {
  // Navigation
  currentView: View;
  setView: (view: View) => void;

  // Loading & Error states
  isLoading: boolean;
  /** true após a PRIMEIRA carga de projetos (sucesso ou erro) — gate dos skeletons. */
  projectsLoaded: boolean;
  error: string | null;
  clearError: () => void;

  // Projects
  projects: Project[];
  currentProjectId: string | null;
  currentChapterId: string | null;

  // Actions - Projects (async)
  fetchProjects: () => Promise<void>;
  createProject: (metadata?: Partial<BookMetadata>, template?: string, kind?: 'book' | 'manga') => Promise<string>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  duplicateProject: (id: string) => Promise<string>;
  setCurrentProject: (id: string | null) => void;

  // Actions - Chapters (async)
  addChapter: (projectId: string, title?: string, sectionType?: SectionType, content?: string) => Promise<string>;
  updateChapter: (projectId: string, chapterId: string, updates: Partial<Chapter>) => void;
  updateChapterDebounced: (projectId: string, chapterId: string, updates: Partial<Chapter>) => void;
  deleteChapter: (projectId: string, chapterId: string) => Promise<void>;
  reorderChapters: (projectId: string, fromIndex: number, toIndex: number) => Promise<void>;
  setCurrentChapter: (id: string | null) => void;

  // Writing Sessions
  currentSession: WritingSession | null;
  startSession: () => void;
  endSession: () => Promise<void>;
  updateSessionWords: (words: number) => void;

  // Writing Goals
  setWritingGoal: (projectId: string, goal: WritingGoal) => void;
  updateGoalProgress: (projectId: string, words: number) => void;

  // Daily Stats
  dailyStats: DailyStats[];
  recordDailyStats: (words: number, time: number) => void;
  getTodayStats: () => DailyStats | undefined;
  getWeekStats: () => DailyStats[];
  fetchDailyStats: () => Promise<void>;

  // Settings
  settings: EditorSettings;
  updateSettings: (updates: Partial<EditorSettings>) => void;

  // Helpers
  getCurrentProject: () => Project | undefined;
  getCurrentChapter: () => Chapter | undefined;
  getTotalWords: (projectId?: string) => number;

  // Data Management
  exportAllData: () => string;
  importData: (data: string) => boolean;
  clearAllData: () => void;
}

const defaultSettings: EditorSettings = {
  theme: 'system',
  autoSave: true,
  autoSaveInterval: 30,
  fontFamily: 'serif',
  fontSize: 18,
  lineHeight: 'normal',
  showWordCount: true,
  showCharCount: false,
  typewriterMode: false,
  focusModeOpacity: 0.3,
  spellcheck: true,
  showChapterNumber: true,
  dropCaps: true,
  canvasTheme: 'night',
  writingEffect: 'stardust',
  aiAutocomplete: false,
  aiAutocompleteLength: 'short',
};

const defaultMetadata: BookMetadata = {
  title: '',
  author: '',
  description: '',
  genre: '',
  language: 'en',
};

const getTodayDateString = () => new Date().toISOString().split('T')[0];

// Debounced API calls map (per chapter)
const debouncedChapterUpdates = new Map<string, ReturnType<typeof debounce>>();

// Get or create debounced update function for a chapter
function getDebouncedChapterUpdate(chapterId: string) {
  if (!debouncedChapterUpdates.has(chapterId)) {
    const debouncedFn = debounce(async (updates: Partial<Chapter>) => {
      try {
        await api.updateChapter(chapterId, mapChapterToApi(updates));
      } catch (error) {
        console.error('Failed to save chapter:', error);
      }
    }, 1000);
    debouncedChapterUpdates.set(chapterId, debouncedFn);
  }
  return debouncedChapterUpdates.get(chapterId)!;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Navigation
      currentView: 'projects',
      setView: (view) => set({ currentView: view }),

      // Loading & Error
      isLoading: false,
      projectsLoaded: false,
      error: null,
      clearError: () => set({ error: null }),

      // Projects
      projects: [],
      currentProjectId: null,
      currentChapterId: null,

      // Fetch all projects from API
      fetchProjects: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.getProjects();
          const projects = (response.projects as ApiProjectWithChapters[]).map(mapProjectFromApi);
          set({ projects, isLoading: false, projectsLoaded: true });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch projects';
          set({ error: message, isLoading: false, projectsLoaded: true });
          throw error;
        }
      },

      // Create project via API
      createProject: async (metadata, template = 'blank', kind = 'book') => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.createProject({
            title: metadata?.title || 'Untitled',
            template,
            kind,
          });
          const project = mapProjectFromApi(response.project as ApiProjectWithChapters);

          set((state) => ({
            projects: [...state.projects, project],
            currentProjectId: project.id,
            currentChapterId: project.chapters[0]?.id ?? null,
            isLoading: false,
          }));

          return project.id;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create project';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Update project via API
      updateProject: async (id, updates) => {
        // Optimistic update — deep-merge metadata so a partial update
        // (e.g. editing the title) does NOT wipe other fields like coverImage.
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === id
              ? {
                  ...p,
                  ...updates,
                  metadata: { ...p.metadata, ...updates.metadata },
                  updatedAt: new Date(),
                }
              : p
          ),
        }));

        try {
          const project = get().projects.find((p) => p.id === id);
          if (!project) return;

          await api.updateProject(id, {
            title: updates.metadata?.title ?? project.metadata.title,
            description: updates.metadata?.description ?? project.metadata.description,
            genre: updates.metadata?.genre ?? project.metadata.genre,
            language: updates.metadata?.language ?? project.metadata.language,
            coverImage: updates.metadata?.coverImage ?? project.metadata.coverImage,
          });
        } catch (error) {
          // Revert on error - refetch from API
          await get().fetchProjects();
          throw error;
        }
      },

      // Delete project via API
      deleteProject: async (id) => {
        const previousProjects = get().projects;

        // Optimistic update
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== id),
          currentProjectId: state.currentProjectId === id ? null : state.currentProjectId,
          currentChapterId: state.currentProjectId === id ? null : state.currentChapterId,
        }));

        try {
          await api.deleteProject(id);
        } catch (error) {
          // Revert on error
          set({ projects: previousProjects });
          throw error;
        }
      },

      // Duplicate project via API
      duplicateProject: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/projects/${id}/duplicate`, {
            method: 'POST',
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error('Failed to duplicate project');
          }

          const data = await response.json();
          const project = mapProjectFromApi(data.project as ApiProjectWithChapters);

          set((state) => ({
            projects: [...state.projects, project],
            isLoading: false,
          }));

          return project.id;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to duplicate project';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      setCurrentProject: (id) => {
        const state = get();
        const project = state.projects.find((p) => p.id === id);

        // Only reset chapter if switching to a different project
        // or if current chapter doesn't exist in this project
        const currentChapterExistsInProject = project?.chapters.some(
          (c) => c.id === state.currentChapterId
        );

        const newChapterId = currentChapterExistsInProject
          ? state.currentChapterId
          : project?.chapters[0]?.id ?? null;

        set({
          currentProjectId: id,
          currentChapterId: newChapterId,
        });
      },

      // Chapter Actions
      addChapter: async (projectId, title, sectionType = 'chapter', content = '') => {
        const project = get().projects.find((p) => p.id === projectId);
        if (!project) throw new Error('Project not found');

        const chapterTitle = title || `Chapter ${project.chapters.filter((c) => c.sectionType === 'chapter').length + 1}`;

        try {
          const response = await api.createChapter({
            projectId,
            title: chapterTitle,
            sectionType,
            content,
          });

          const chapter = mapChapterFromApi(response.chapter);

          set((state) => ({
            projects: state.projects.map((p) => {
              if (p.id !== projectId) return p;
              return {
                ...p,
                chapters: [...p.chapters, chapter],
                updatedAt: new Date(),
              };
            }),
            currentChapterId: chapter.id,
          }));

          return chapter.id;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create chapter';
          set({ error: message });
          throw error;
        }
      },

      // Immediate local update (for UI responsiveness)
      updateChapter: (projectId, chapterId, updates) => {
        set((state) => ({
          projects: state.projects.map((p) => {
            if (p.id !== projectId) return p;
            return {
              ...p,
              chapters: p.chapters.map((c) =>
                c.id === chapterId ? { ...c, ...updates, updatedAt: new Date() } : c
              ),
              updatedAt: new Date(),
            };
          }),
        }));
      },

      // Debounced API update (call this for content changes)
      updateChapterDebounced: (projectId, chapterId, updates) => {
        // Update local state immediately
        get().updateChapter(projectId, chapterId, updates);

        // Debounced API call
        const debouncedUpdate = getDebouncedChapterUpdate(chapterId);
        debouncedUpdate(updates);
      },

      deleteChapter: async (projectId, chapterId) => {
        const project = get().projects.find((p) => p.id === projectId);
        if (!project || project.chapters.length <= 1) return;

        const deletedIndex = project.chapters.findIndex((c) => c.id === chapterId);
        const newChapters = project.chapters
          .filter((c) => c.id !== chapterId)
          .map((c, i) => ({ ...c, order: i }));

        const newCurrentChapter =
          get().currentChapterId === chapterId
            ? newChapters[Math.min(deletedIndex, newChapters.length - 1)]?.id ?? null
            : get().currentChapterId;

        // Optimistic update
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, chapters: newChapters, updatedAt: new Date() } : p
          ),
          currentChapterId: newCurrentChapter,
        }));

        try {
          await api.deleteChapter(chapterId);
          // Clean up debounced function
          debouncedChapterUpdates.delete(chapterId);
        } catch (error) {
          // Revert on error
          await get().fetchProjects();
          throw error;
        }
      },

      reorderChapters: async (projectId, fromIndex, toIndex) => {
        const project = get().projects.find((p) => p.id === projectId);
        if (!project) return;

        const chapters = [...project.chapters];
        const [moved] = chapters.splice(fromIndex, 1);
        chapters.splice(toIndex, 0, moved);
        const reorderedChapters = chapters.map((c, i) => ({ ...c, order: i }));

        // Optimistic update
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, chapters: reorderedChapters, updatedAt: new Date() } : p
          ),
        }));

        try {
          const chapterIds = reorderedChapters.map((c) => c.id);
          await api.reorderChapters(projectId, chapterIds);
        } catch (error) {
          // Revert on error
          await get().fetchProjects();
          throw error;
        }
      },

      setCurrentChapter: (id) => set({ currentChapterId: id }),

      // Writing Sessions
      currentSession: null,

      startSession: () => {
        const now = new Date();
        set({
          currentSession: {
            id: uuid(),
            startTime: now,
            wordsWritten: 0,
          },
        });
      },

      endSession: async () => {
        const state = get();
        if (!state.currentSession || !state.currentProjectId) return;

        const session: WritingSession = {
          ...state.currentSession,
          endTime: new Date(),
        };

        // Update local state
        set((s) => ({
          projects: s.projects.map((p) =>
            p.id === state.currentProjectId
              ? { ...p, sessions: [...p.sessions, session] }
              : p
          ),
          currentSession: null,
        }));

        const duration = session.endTime
          ? (session.endTime.getTime() - session.startTime.getTime()) / 1000 / 60
          : 0;

        // Record to local stats
        get().recordDailyStats(session.wordsWritten, duration);

        // Try to sync with API
        try {
          await api.recordSession({
            projectId: state.currentProjectId,
            wordsWritten: session.wordsWritten,
            duration: Math.round(duration),
          });
        } catch (error) {
          console.error('Failed to sync session:', error);
        }
      },

      updateSessionWords: (words) => {
        set((state) => ({
          currentSession: state.currentSession
            ? { ...state.currentSession, wordsWritten: state.currentSession.wordsWritten + words }
            : null,
        }));
      },

      // Writing Goals
      setWritingGoal: (projectId, goal) => {
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, writingGoal: goal } : p
          ),
        }));
      },

      updateGoalProgress: (projectId, words) => {
        set((state) => ({
          projects: state.projects.map((p) => {
            if (p.id !== projectId || !p.writingGoal) return p;
            return {
              ...p,
              writingGoal: {
                ...p.writingGoal,
                currentWords: p.writingGoal.currentWords + words,
              },
            };
          }),
        }));
      },

      // Daily Stats
      dailyStats: [],

      recordDailyStats: (words, time) => {
        const today = getTodayDateString();
        set((state) => {
          const existingIndex = state.dailyStats.findIndex((s) => s.date === today);

          if (existingIndex >= 0) {
            const updated = [...state.dailyStats];
            updated[existingIndex] = {
              ...updated[existingIndex],
              wordsWritten: updated[existingIndex].wordsWritten + words,
              timeSpent: updated[existingIndex].timeSpent + time,
              sessionsCount: updated[existingIndex].sessionsCount + 1,
            };
            return { dailyStats: updated };
          }

          return {
            dailyStats: [
              ...state.dailyStats,
              { date: today, wordsWritten: words, timeSpent: time, sessionsCount: 1 },
            ],
          };
        });
      },

      getTodayStats: () => {
        const today = getTodayDateString();
        return get().dailyStats.find((s) => s.date === today);
      },

      getWeekStats: () => {
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return get().dailyStats.filter((s) => new Date(s.date) >= weekAgo);
      },

      fetchDailyStats: async () => {
        try {
          const response = await api.getDailyStats();
          set({ dailyStats: response.stats });
        } catch (error) {
          console.error('Failed to fetch daily stats:', error);
        }
      },

      // Settings
      settings: defaultSettings,

      updateSettings: (updates) => {
        set((state) => ({
          settings: { ...state.settings, ...updates },
        }));
      },

      // Helpers
      getCurrentProject: () => {
        const state = get();
        return state.projects.find((p) => p.id === state.currentProjectId);
      },

      getCurrentChapter: () => {
        const state = get();
        const project = state.getCurrentProject();
        return project?.chapters.find((c) => c.id === state.currentChapterId);
      },

      getTotalWords: (projectId) => {
        const state = get();
        const project = projectId
          ? state.projects.find((p) => p.id === projectId)
          : state.getCurrentProject();
        return project?.chapters.reduce((sum, ch) => sum + ch.wordCount, 0) ?? 0;
      },

      // Data Management
      exportAllData: () => {
        const state = get();
        return JSON.stringify(
          {
            projects: state.projects,
            settings: state.settings,
            dailyStats: state.dailyStats,
            exportedAt: new Date().toISOString(),
            version: '1.0.0',
          },
          null,
          2
        );
      },

      importData: (data) => {
        try {
          const parsed = JSON.parse(data);
          if (parsed.projects && Array.isArray(parsed.projects)) {
            set({
              projects: parsed.projects,
              settings: parsed.settings || defaultSettings,
              dailyStats: parsed.dailyStats || [],
            });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },

      clearAllData: () => {
        set({
          projects: [],
          currentProjectId: null,
          currentChapterId: null,
          currentSession: null,
          dailyStats: [],
          settings: defaultSettings,
        });
      },
    }),
    {
      name: 'articulat-storage',
      // Only persist settings and dailyStats locally - projects come from API
      partialize: (state) => ({
        settings: state.settings,
        dailyStats: state.dailyStats,
      }),
      // Deep-merge persisted settings over defaults so newly-added fields keep
      // their default, and migrate removed/invalid writing effects (e.g. the
      // retired 'ink') to a safe current one.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as { settings?: Partial<typeof defaultSettings> };
        const settings = { ...current.settings, ...(p.settings ?? {}) };
        const VALID_EFFECTS = [
          'petals', 'stardust', 'inkrain', 'sparks', 'glitch',
          'mist', 'fire', 'fireflies', 'neon', 'glyphs', 'none',
        ];
        if (!VALID_EFFECTS.includes(settings.writingEffect)) {
          settings.writingEffect = 'stardust';
        }
        return { ...current, ...(persisted as object), settings };
      },
    }
  )
);
